// src/pages/Landing.jsx
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button";
import { isAuthed } from "../auth";
import { useTranslation } from "react-i18next";

export default function Landing() {
  const nav = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (isAuthed()) nav("/app/dashboard", { replace: true });
  }, [nav]);

  return (
    <div className="min-h-screen bg-white">
      {/* Simple header */}
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          {/* Favicon “logo” with gentle crop to reduce white padding */}
          <div className="font-bold text-blue-700 tracking-tight">Zeroriscuri</div>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" onClick={() => nav("/login")}>
              {t("landing.sign_in")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        <section className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left gap-8 sm:gap-16">
          {/* Left image (5× bigger, no border) */}
          <img
            src="/favicon.png"
            alt="Zeroriscuri logo"
            className="h-96 w-96 object-contain rounded-lg"
          />

          {/* Right text content */}
          <div className="space-y-6">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
              {t("landing.h1")}
            </h1>
            <h2 className="text-lg sm:text-xl text-slate-600 max-w-3xl">
              {t("landing.h2")}
            </h2>

            <div className="flex justify-center sm:justify-start">
              <Button size="lg" onClick={() => nav("/login")}>
                {t("landing.cta")}
              </Button>
            </div>

            <p className="text-xs text-slate-400 max-w-2xl">
              {t("landing.subtext")}
            </p>
          </div>
        </section>

        {/* Quick features/benefits */}
        <section className="mt-12 grid sm:grid-cols-3 gap-4">
          <Feature
            title={t("landing.feat1_title")}
            text={t("landing.feat1_text")}
          />
          <Feature
            title={t("landing.feat2_title")}
            text={t("landing.feat2_text")}
          />
          <Feature
            title={t("landing.feat3_title")}
            text={t("landing.feat3_text")}
          />
        </section>
      </main>

      <footer className="border-t border-slate-200 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-wrap items-center justify-between text-xs text-slate-400 gap-3">
          {/* Footer image */}
          <img
            src="/work_management_logo.jpeg"
            alt="Work Management"
            className="h-10 w-auto"
            loading="lazy"
          />
          <span>
            © {new Date().getFullYear()} Zeroriscuri · Work Management
          </span>
        </div>
      </footer>
    </div>
  );
}

function Feature({ title, text }) {
  return (
    <div className="p-5 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-white hover:shadow-sm transition-all">
      <div className="font-semibold text-slate-900 mb-1">{title}</div>
      <div className="text-sm text-slate-600">{text}</div>
    </div>
  );
}
